import { ArrowSmallDownIcon, ArrowSmallUpIcon } from '@heroicons/react/24/solid'
import { Header, HeaderGroup, Table, flexRender } from '@tanstack/react-table'

type Props = {
    table: Table<any>
}

/**
 * This is the most basic Arwen table and is used for displaying tabstack/react-table
 * data. It is independent of the shape of the data being displayed.
 */
export default function SimpleTable(props: Props) {
    const { table } = props

    return (
        // Shared table chrome so admin tables read as one visual language — the
        // same treatment as the profile list (AccountList): a solid tinted header
        // band, ink-200 hairline beneath it, and ink-100 dividers between rows.
        <div className="overflow-x-auto rounded-lg border border-ink-200 bg-white shadow-sm">
            <table className="min-w-full table-fixed border-collapse text-sm">
                <thead className="bg-ink-50">
                    {table.getHeaderGroups().map((headerGroup: HeaderGroup<any>) => (
                        <tr key={headerGroup.id} className="border-b border-ink-200">
                            {headerGroup.headers.map((header: Header<any, any>) => {
                                const width = header.getSize() !== 150 ? header.getSize() : undefined
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        className="px-4 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-ink-500"
                                        style={width ? { width: `${width}%` } : undefined}
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div
                                                {...{
                                                    className: header.column.getCanSort()
                                                        ? 'cursor-pointer select-none'
                                                        : '',
                                                    onClick: header.column.getToggleSortingHandler()
                                                }}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                {{
                                                    asc: (
                                                        <ArrowSmallUpIcon className="mb-1 ml-1 h-4 w-4 inline" />
                                                    ),
                                                    desc: (
                                                        <ArrowSmallDownIcon className="mb-1 ml-1 h-4 w-4 inline" />
                                                    )
                                                }[header.column.getIsSorted() as string] ?? null}
                                            </div>
                                        )}
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody className="divide-y divide-ink-100 bg-white">
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="hover:bg-ink-50">
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className="px-4 py-2 text-sm text-ink-700">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    {table.getFooterGroups().map((footerGroup) => (
                        <tr key={footerGroup.id}>
                            {footerGroup.headers.map((header) => (
                                <th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                              header.column.columnDef.footer,
                                              header.getContext()
                                          )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </tfoot>
            </table>
        </div>
    )
}
